/* Verbindet Asset-Selectbox und -Browser mit dem Editor (geladene WADs, Sprites, Skybox, Sounds) und dem Server (Liste, Dateien).
 * AssetApp ist der Teil des Editors, den diese Schicht braucht; AssetHost (host.ts) ist, was die Oberfläche sieht. */
import Spr from '../../formats/spr.ts';
import Wad from '../../formats/wad.ts';
import type { Pixels, WadFile } from '../../formats/wad.ts';
import I18n from '../../i18n.ts';
import { buildEntries, isMissing, skySidesOnServer, unclassified } from '../../model/assets.ts';
import type { AssetEntry, ServerAsset } from '../../model/assets.ts';
import { expectedWads, modelNeeds, soundNeeds, spriteNeeds, usedInfo } from '../../model/texlist.ts';
import { modelKey } from '@xen-forge/mdl';
import type { Doc } from '../../model/types.ts';
import { PreviewCache, pickSkyPath, thumbnail } from '../../net/previews.ts';
import type { BackendSession } from '../../net/session.ts';
import { lookupSound } from '../../net/sounds.ts';
import { openDialog } from '../modal.tsx';
import { AssetBrowser } from './AssetBrowser.tsx';
import type { AssetHost, BrowserTab, WadInfo } from './host.ts';

export interface AssetApp {
  doc: Doc;
  wads: { count(): number; names(): string[]; has(n: string): boolean; pixels(n: string): Pixels | null; wads: WadFile[] };
  wadNames: string[];
  sprites: Map<string, { w: number; h: number; rgba: Uint8ClampedArray }>;
  sounds: Map<string, { buf: AudioBuffer }>;
  /** geladene Modelle (Schlüssel = Dateiname klein) und Laden aus Dateien (mit Begleitdateien) */
  modelKeys(): string[];
  addModelFiles(files: File[]): Promise<void>;
  skybox: { name: string; imgs: Record<string, Pixels>; preview?: boolean };
  bsp: { scene: { polys: { tex: string; special: boolean }[] }; data: { textures: { name: string; rgba: unknown }[] } } | null;
  activeTex: string;
  soundKey(m: unknown): string;
  hasSound(key: string): boolean;
  getAudio(): AudioContext | null;
  setActiveTex(name: string, apply?: boolean): void;
  openFile(file: File): Promise<void>;
  addSound(file: File, key?: string): Promise<void>;
  addSkyFiles(files: File[], opts?: { preview?: boolean }): Promise<void>;
  setSkyPreview(on: boolean): void;
  status(msg: string): void;
}

const baseName = (p: string): string => p.replace(/^.*\//, '');
const SAMPLE = 12;

export function createAssetHost(session: BackendSession, app: AssetApp): AssetHost {
  const listeners = new Set<() => void>();
  const notify = () => listeners.forEach((f) => f());
  let server: ServerAsset[] = [];
  let cache: PreviewCache | null = null;
  const decoded = new Map<string, AudioBuffer>();
  let running: Promise<number> | null = null;

  const usable = () => { const s = session.getSnapshot(); return !!session.client && s.state === 'ok' && s.authed; };
  const previews = (): PreviewCache => {
    if (!cache) { const c = session.client; cache = new PreviewCache((p) => { if (!c) throw new Error('kein Backend'); return c.fetchBuffer(p); }); }
    return cache;
  };
  // Neue Anmeldung oder anderer Server: Liste und Vorschauen verwerfen
  let autoFor = false;
  session.subscribe(() => {
    cache = null; decoded.clear();
    const ok = usable();
    if (ok) { void host.refresh().then(() => { if (!autoFor) void host.autoLoad(); autoFor = true; }); } else { autoFor = false; server = []; notify(); }
  });

  const state = () => {
    const w = app.doc.ents[0].kv;
    return {
      wantedWads: expectedWads(w.wad), loadedWadNames: app.wadNames,
      wantedSprites: Array.from(spriteNeeds(app.doc, Spr.key).keys()), loadedSprites: Array.from(app.sprites.keys()),
      wantedSky: String(w.skyname || ''), loadedSky: app.skybox.name ? { name: app.skybox.name, sides: Object.keys(app.skybox.imgs).length } : null,
      wantedSounds: Array.from(soundNeeds(app.doc, app.soundKey).keys()), loadedSounds: Array.from(app.sounds.keys()), hasSound: (k: string) => app.hasSound(k),
      wantedModels: Array.from(modelNeeds(app.doc, modelKey).keys()), loadedModels: app.modelKeys()
    };
  };

  const fetchFiles = async (e: AssetEntry): Promise<File[]> => {
    const c = session.client; if (!c) throw new Error('kein Backend');
    return Promise.all(e.paths.map(async (p) => new File([await c.fetchBuffer(p)], baseName(p))));
  };

  const host: AssetHost = {
    server: usable,
    entries: () => buildEntries(server, state()),
    async refresh() {
      const c = session.client;
      if (!c || !usable()) { server = []; notify(); return; }
      try { server = (await c.list()).map((a) => ({ path: a.path, size: a.size })); } catch { /* Liste bleibt, wie sie war */ }
      notify();
    },
    subscribe(f) { listeners.add(f); return () => { listeners.delete(f); }; },
    unclassified: () => unclassified(server),
    async removeMany(paths) {
      const c = session.client; if (!c) return 0;
      let done = 0, failed = 0, last = '';
      for (const path of paths) {
        try { await c.remove(path); done++; } catch (err) { failed++; last = path + ': ' + (err as Error).message; }
      }
      await host.refresh();
      app.status(failed ? I18n.t('ab.deleted_failed', { done, failed, last }) : I18n.t('ab.deleted', { count: done }));
      return done;
    },
    textures() {
      const used = usedInfo(app.doc, app.bsp ? { polys: app.bsp.scene.polys, textures: app.bsp.data.textures } : null, (n) => app.wads.has(n));
      return { loaded: app.wads.names(), used, active: app.activeTex, version: app.wadNames.join('|') };
    },
    texPixels: (n) => app.wads.pixels(n),
    setActiveTex: (n, apply) => { app.setActiveTex(n, apply); notify(); },

    async preview(e) {
      if (e.kind === 'sprite') {
        const s = app.sprites.get(e.key);
        if (s) return thumbnail({ w: s.w, h: s.h, rgba: s.rgba }, 128);
        return e.paths[0] ? previews().sprite(e.paths[0]) : null;
      }
      if (e.kind === 'skybox') {
        const im = app.skybox.name === e.key ? (app.skybox.imgs.ft || Object.values(app.skybox.imgs)[0]) : undefined;
        if (im) return thumbnail(im, 128);
        const p = pickSkyPath(e.paths);
        return p ? previews().skySide(p) : null;
      }
      return null;
    },
    async wadInfo(e): Promise<WadInfo | null> {
      const loaded = app.wads.wads.find((w) => w.name.toLowerCase() === e.key);
      let wad: WadFile | undefined = loaded;
      if (!wad && e.paths[0]) wad = await previews().wad(e.paths[0]);
      if (!wad) return null;
      const w = wad;
      const sample = Array.from(w.entries.values()).slice(0, SAMPLE).map((en) => {
        let px: Pixels | null = null;
        try { px = thumbnail(Wad.decode(w, en), 64); } catch { /* beschädigter Eintrag: ohne Bild */ }
        return { name: en.name, px };
      });
      return { count: w.entries.size, sample };
    },

    async load(e, opts) {
      try {
        if (e.kind === 'skybox' && opts?.preview && app.skybox.name === e.key && Object.keys(app.skybox.imgs).length >= 6) { app.setSkyPreview(true); notify(); return true; }
        const files = await fetchFiles(e);
        if (e.kind === 'skybox') await app.addSkyFiles(files, { preview: !!opts?.preview });
        else if (e.kind === 'sound') await app.addSound(files[0], e.key);
        else if (e.kind === 'model') await app.addModelFiles(files);
        else await app.openFile(files[0]);
        notify();
        return true;
      } catch (err) { app.status(I18n.t('be.load_failed', { path: e.paths[0] || e.key, msg: (err as Error).message })); return false; }
    },

    async play(e, onEnd) {
      const ac = app.getAudio(); if (!ac) { app.status(I18n.t('ui.no_audio')); throw new Error('kein Audio'); }
      let buf: AudioBuffer | undefined = lookupSound(app.sounds, e.key)?.buf;
      if (!buf && e.paths[0]) {
        buf = decoded.get(e.paths[0]);
        if (!buf) { buf = await ac.decodeAudioData((await previews().buffer(e.paths[0])).slice(0)); decoded.set(e.paths[0], buf); }
      }
      if (!buf) throw new Error('kein Sound');
      const src = ac.createBufferSource(); src.buffer = buf; src.connect(ac.destination);
      src.onended = onEnd; src.start();
      return () => { src.onended = null; try { src.stop(); } catch { /* schon beendet */ } };
    },

    // Läuft schon ein Durchgang, wird kein zweiter gestartet (Karte geöffnet und FGD angekommen lösen es fast gleichzeitig aus)
    autoLoad() {
      if (running) return running;
      running = (async () => {
        if (!usable()) return 0;
        await host.refresh();
        let n = 0;
        for (const e of host.entries()) {
          if (!isMissing(e) || !e.onServer || (e.kind !== 'sprite' && e.kind !== 'skybox' && e.kind !== 'model')) continue;
          if (e.kind === 'skybox' && skySidesOnServer(e).length < 6) { app.status(I18n.t('ab.sky_incomplete', { name: e.label, have: skySidesOnServer(e).length })); continue; }
          if (await host.load(e)) n++;
        }
        if (n) app.status(I18n.t('ab.autoloaded', { count: n }));
        return n;
      })().finally(() => { running = null; });
      return running;
    },

    open(tab?: BrowserTab) {
      void openDialog((close) => <AssetBrowser host={host} initialTab={tab} onClose={() => close()} />);
    }
  };
  return host;
}
